import React, { useEffect, useMemo, useState } from 'react';
import { ControlWrapper, Slider } from '../../../../../../../components';
import { ChannelTypeInternal, GateStateDirectionInternal } from '../../../../../../../data-access/gql-types/graphql';
import { useChannel } from '../../../../../components/channel/hooks/use-channel';
import { GateControls } from '../../../../../components/gate-controls';
import { useDevicesAndChannelsContext } from '../../../../../context/devices-and-channels';
import { useGate } from '../../../../../hooks/gate';
import { useChannelsStateStore } from '../../../../../store/channels-state';
import { ChannelType } from '../../../../../types';
import { ChannelGateStateType, ChannelGateType } from '../../../../../types';
import { useGateSupportedFeatures } from '../../hooks/use-gate-supported';
import { checkIfMicroventilationVisible } from '../../utils';
import { AdditionalModes } from '../additional-modes';
import { GateCalibrationInfo } from '../calibration-info/calibration-info';

type GateRollProps = {
  channel: ChannelType;
  setPreventSwipeViews: React.Dispatch<React.SetStateAction<boolean>>;
};

export const GateRoll: React.FC<GateRollProps> = ({ channel, setPreventSwipeViews }) => {
  const { handleAction, handleVentilation, gateActive } = useGate(channel);
  const { calibrationNeeded } = useChannel({ channel });
  const { channelList } = useDevicesAndChannelsContext();
  const [sliderValue, setSliderValue] = useState<number>(0);
  const [targetValue, setTargetValue] = useState<number>(0);
  const { setDirectionSupported, setPositionSupported, ventilationSupported } = useGateSupportedFeatures(channel);
  const state = useChannelsStateStore((s) => s.channelState[channel.id]) as ChannelGateStateType | null;

  if (channel.data.type !== ChannelTypeInternal.Gate) return null;

  useEffect(() => {
    if (state?.movingParams?.targetPosition != null) {
      setTargetValue(state.movingParams.targetPosition);
    } else {
      if (
        state?.position != null &&
        (state.direction === GateStateDirectionInternal.Stopped ||
          state.direction === GateStateDirectionInternal.Open ||
          state.direction === GateStateDirectionInternal.Closed)
      ) {
        setTargetValue(state.position);
      }
    }
  }, [state?.movingParams?.targetPosition, state?.direction]);

  useEffect(() => {
    setSliderValue(state?.position || 0);
  }, [state?.position]);

  useEffect(() => {
    const timeout = setTimeout(() => setPreventSwipeViews(false), 950);
    return () => clearTimeout(timeout);
  }, [targetValue]);

  const dependantChannels = useMemo(
    () =>
      (channel.data as ChannelGateType).dependantChannels.filter((x) =>
        channelList.find((y) => y.id === x.channelId && y.isVisible),
      ),
    [(channel.data as ChannelGateType).dependantChannels, channelList],
  );

  const handleSliderEvent = (event: React.PointerEvent<HTMLInputElement>) => {
    handleAction(Number((event.target as HTMLInputElement).value));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreventSwipeViews(true);
    setTargetValue(Number(event.target.value));
  };

  const microventilationVisible = checkIfMicroventilationVisible(ventilationSupported, calibrationNeeded);

  return (
    <>
      <ControlWrapper className="control-wrapper--full-space ">
        <div>
          {!!(setDirectionSupported || microventilationVisible || dependantChannels.length) && (
            <GateControls
              kind="channel"
              channel={channel}
              open={() => {
                handleAction('open');
                setTargetValue(0);
              }}
              close={() => {
                handleAction('close');
                setTargetValue(100);
              }}
              stop={() => handleAction('stop')}
              ventilation={handleVentilation}
              ventilationVisible={microventilationVisible}
              directionVisible={setDirectionSupported}
              dependantChannels={dependantChannels}
              active={gateActive}
            />
          )}
          {setPositionSupported && !calibrationNeeded && (
            <Slider
              onPointerUp={handleSliderEvent}
              value={sliderValue}
              targetValue={targetValue}
              onChange={onChange}
              position="horizontal"
              animationWhenActive
              noBgElement
            />
          )}
          <AdditionalModes channel={channel} />
        </div>
      </ControlWrapper>
      {calibrationNeeded && <GateCalibrationInfo channel={channel} />}
    </>
  );
};
